/**
 * LocalDocumentAdapter.ts — the pure-frontend document session.
 *
 * For local mode (no API connected): the frontend mints the doc id, stores the
 * bundle in `StorageBackend` under `bs.bundle.<docId>`, and applies patches in
 * memory. There's no server to broadcast to, so `subscribe` fires only on the
 * caller's own edits (single-user) — but the surface is identical to the remote
 * adapter, so the React layer (`useDocument`) doesn't branch.
 *
 * Patch application reuses the engine's `LocalAdapter` — it already implements
 * every edit op (addNode/renameNode/addMetric/…) with the exact semantics the
 * editor expects. We instantiate one transient `LocalAdapter` per open doc as
 * the patch applier, apply the op, and read back `exportBundle()`. The sim
 * itself is driven separately by `useSim`; this adapter only concerns itself
 * with the *document* (the saved bundle shape).
 */
import type { EditOpName, KnobPin, LegendEntry, ScenarioBundle, WorkflowTrigger } from '../engine/types';
import { LocalAdapter } from '../engine/adapter';
import type { StorageBackend } from '../storage/StorageBackend';
import { uuid } from '../library/ids';

import { DocumentAdapter, DocPatch, DocSession } from './DocumentAdapter';

const DOC_BUNDLE_PREFIX = 'bs.bundle.';
const DOC_META_PREFIX = 'bs.docmeta.';

/** Minimal meta persisted alongside the bundle so the library + open-doc views
 *  can show label/nodeCount/updatedAt without a separate index. */
interface LocalDocMeta {
  id: string;
  label: string;
  nodeCount: number;
  updatedAt: number;
  createdAt: number;
}

export interface LocalDocumentAdapterOpts {
  /** Storage to read/write bundles + meta from. */
  storage: StorageBackend;
}

export class LocalDocumentAdapter implements DocumentAdapter {
  /** docId → the in-memory patch applier. Lazily created on open(). */
  private appliers = new Map<string, LocalAdapter>();
  private subscribers = new Map<string, Set<(bundle: ScenarioBundle) => void>>();

  constructor(private readonly opts: LocalDocumentAdapterOpts) {}

  async create(seed?: Partial<ScenarioBundle>): Promise<{ docId: string }> {
    const docId = uuid(); // frontend mints the id in local mode
    const now = Date.now();
    const bundle: ScenarioBundle = {
      id: docId,
      label: seed?.label ?? 'Untitled',
      description: seed?.description,
      graph: seed?.graph ?? { nodes: [], edges: [] },
      metrics: seed?.metrics ?? [],
      initialParams: seed?.initialParams ?? {},
      initialStocks: seed?.initialStocks,
      groups: seed?.groups,
    };
    this.opts.storage.saveBundle(bundle);
    this.writeMeta(docId, {
      id: docId,
      label: bundle.label,
      nodeCount: bundle.graph.nodes.length,
      updatedAt: now,
      createdAt: now,
    });
    return { docId };
  }

  async open(docId: string): Promise<DocSession> {
    // A saved doc in localStorage.
    const bundle = this.opts.storage.loadBundle(docId);
    if (!bundle) {
      return { docId, bundle: null, status: 'error', error: `No document with id '${docId}'.` };
    }
    // Spin up the patch applier seeded with the loaded bundle.
    this.appliers.set(docId, new LocalAdapter(bundle));
    return { docId, bundle, status: 'ready' };
  }

  patch(docId: string, patch: DocPatch): void {
    const applier = this.ensureApplier(docId);
    if (!applier) return;
    switch (patch.op) {
      case 'replaceBundle':
        applier.replaceBundle(patch.bundle);
        break;
      case 'edit':
        // Target the right graph for per-graph ops (collection ops ignore it).
        if (patch.graphId && patch.graphId !== applier.activeGraphId()) {
          applier.setActiveGraph?.(patch.graphId);
        }
        this.applyEdit(applier, patch.name, patch.args);
        break;
      case 'setParam':
        applier.setParam(patch.path, patch.value);
        // setParam doesn't fire a bundle change on the engine (params live
        // apart from the bundle); persist the value onto initialParams so a
        // reload reflects it.
        this.persistParam(docId, patch.path, patch.value);
        break;
    }
    if (patch.op !== 'setParam') {
      const next = applier.exportBundle();
      this.persist(docId, next);
      this.fire(docId, next);
    } else {
      // setParam still wants to notify subscribers (the value moved), but the
      // bundle shape is unchanged — fire the current bundle.
      this.fire(docId, applier.exportBundle());
    }
  }

  subscribe(docId: string, onUpdate: (bundle: ScenarioBundle) => void): () => void {
    let subs = this.subscribers.get(docId);
    if (!subs) {
      subs = new Set();
      this.subscribers.set(docId, subs);
    }
    subs.add(onUpdate);
    return () => {
      subs?.delete(onUpdate);
    };
  }

  async save(docId: string, bundle: ScenarioBundle): Promise<void> {
    // Re-seed the applier so subsequent patches build on the saved state.
    this.appliers.set(docId, new LocalAdapter(bundle));
    this.persist(docId, bundle);
    this.fire(docId, bundle);
  }

  close(docId: string): void {
    this.appliers.delete(docId);
    this.subscribers.delete(docId);
  }

  // ── Internal ───────────────────────────────────────────────────────────
  private ensureApplier(docId: string): LocalAdapter | null {
    const existing = this.appliers.get(docId);
    if (existing) return existing;
    const bundle = this.opts.storage.loadBundle(docId);
    if (!bundle) return null;
    const applier = new LocalAdapter(bundle);
    this.appliers.set(docId, applier);
    return applier;
  }

  /** Dispatch an edit op by name onto the applier. Mirrors `EditOpName`. */
  private applyEdit(applier: LocalAdapter, name: EditOpName, args: unknown[]): void {
    type ApplierMethod = (a: LocalAdapter, args: unknown[]) => void;
    const dispatch: Record<EditOpName, ApplierMethod> = {
      addNode: (a, [node]) => a.addNode?.(node as never),
      updateNode: (a, [node]) => a.updateNode?.(node as never),
      removeNode: (a, [id]) => a.removeNode?.(id as string),
      duplicateNode: (a, [id]) => a.duplicateNode?.(id as string),
      renameNode: (a, [oldId, newId]) => a.renameNode?.(oldId as string, newId as string),
      addEdge: (a, [edge]) => a.addEdge?.(edge as never),
      updateEdge: (a, [edge]) => a.updateEdge?.(edge as never),
      removeEdge: (a, [id]) => a.removeEdge?.(id as string),
      renameEdge: (a, [oldId, newId]) => a.renameEdge?.(oldId as string, newId as string),
      setEdgeGroup: (a, [id, group]) => a.setEdgeGroup?.(id as string, group as string | undefined),
      updateEdgeHandles: (a, [id, next]) => a.updateEdgeHandles?.(id as string, next as never),
      setBends: (a, [id, bends]) => a.setBends?.(id as string, bends as never),
      setLabelT: (a, [id, t]) => a.setLabelT?.(id as string, t as number),
      addGroup: (a, [group]) => a.addGroup?.(group as string),
      addNodeGroup: (a, [group]) => a.addNodeGroup?.(group as never),
      updateNodeGroup: (a, [group]) => a.updateNodeGroup?.(group as never),
      removeNodeGroup: (a, [id]) => a.removeNodeGroup?.(id as string),
      renameGroup: (a, [oldId, newId]) => a.renameGroup?.(oldId as string, newId as string),
      addMetric: (a, [m]) => a.addMetric?.(m as never),
      updateMetric: (a, [m]) => a.updateMetric?.(m as never),
      removeMetric: (a, [key]) => a.removeMetric?.(key as string),
      renameMetric: (a, [oldKey, newKey]) => a.renameMetric?.(oldKey as string, newKey as string),
      setParamKey: (a, [target, id, oldKey, newKey]) =>
        a.setParamKey?.(target as 'node' | 'edge', id as string, oldKey as string, newKey as string),
      setLegend: (a, [legend]) => a.setLegend?.(legend as LegendEntry[]),
      setKnobs: (a, [knobs]) => a.setKnobs?.(knobs as KnobPin[]),
      setWorkflowTriggers: (a, [triggers]) => a.setWorkflowTriggers?.(triggers as WorkflowTrigger[]),
      replaceBundle: (a, [bundle]) => a.replaceBundle?.(bundle as ScenarioBundle),
      // graph-collection ops (multi-graph): mutate the adapter's graph list.
      addGraph: (a, [name]) => { a.addGraph?.(name as string); },
      removeGraph: (a, [graphId]) => { a.removeGraph?.(graphId as string); },
      renameGraph: (a, [graphId, name]) => { a.renameGraph?.(graphId as string, name as string); },
      exportBundle: () => {
        /* read-only op; nothing to apply */
      },
    };
    dispatch[name]?.(applier, args);
  }

  private persist(docId: string, bundle: ScenarioBundle): void {
    this.opts.storage.saveBundle(bundle);
    const meta = this.readMeta(docId);
    this.writeMeta(docId, {
      id: docId,
      label: bundle.label,
      nodeCount: bundle.graph.nodes.length,
      updatedAt: Date.now(),
      createdAt: meta?.createdAt ?? Date.now(),
    });
  }

  private persistParam(docId: string, path: string, value: number): void {
    const bundle = this.opts.storage.loadBundle(docId);
    if (!bundle) return;
    // initialParams is keyed by `<nodeId>.<key>` / `e:<edgeId>.<key>`.
    const initialParams = { ...(bundle.initialParams ?? {}) };
    const spec = initialParams[path];
    if (spec && spec.value !== value) {
      initialParams[path] = { ...spec, value };
      const next = { ...bundle, initialParams };
      this.opts.storage.saveBundle(next);
    }
  }

  private fire(docId: string, bundle: ScenarioBundle): void {
    const subs = this.subscribers.get(docId);
    if (!subs) return;
    for (const fn of subs) fn(bundle);
  }

  private metaKey(docId: string): string {
    return DOC_META_PREFIX + docId;
  }
  private readMeta(docId: string): LocalDocMeta | null {
    return this.opts.storage.get<LocalDocMeta | null>(this.metaKey(docId), null);
  }
  private writeMeta(docId: string, meta: LocalDocMeta): void {
    this.opts.storage.set(this.metaKey(docId), meta);
  }
}

// Exported for tests / the library bridge. Not part of the public port.
export const DOC_KEYS = { bundlePrefix: DOC_BUNDLE_PREFIX, metaPrefix: DOC_META_PREFIX };
